import React from "react";
import {
  TableCell,
  Checkbox,
  Table,
  TableBody,
  TableRow,
} from "@material-ui/core";
import { convertDate } from "../../utils/dateConverter";

const checkIfNullable = (item) => {
  return item ?? "Brak Danych";
};

const checkIfNullableAndRound = (item) => {
  if (item == null) {
    return "Brak Danych";
  }

  if (typeof item !== "number" || isNaN(item)) {
    return "Brak Danych";
  }

  return Math.round(item * 10) / 10;
};

export const baseConfig = {
  mainRowCells: [
    (data) => (
      <TableCell key="base">
        {data.components[0]?.deviceCalibration.base
          ? data.components[0].deviceCalibration.base
          : null}
      </TableCell>
    ),
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Producent",
    "500Hz",
    "1000Hz",
    "2000Hz",
    "4000Hz",
  ],
  collapsibleRowRender: (
    component,
    index,
    { mainCalibrations, checkBoxes, handleCheckboxChange, isDetailedData }
  ) => {
    if (!component.deviceCalibration) {
      return (
        <TableCell colSpan={8}>
          Brak danych kalibracyjnych dla tego komponentu.
        </TableCell>
      );
    }

    const componentId = component.deviceCalibration.id;
    return (
      <React.Fragment key={index}>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          <Checkbox
            key={componentId}
            checked={checkBoxes[componentId] ?? false}
            onChange={() => handleCheckboxChange(componentId)}
          />
        </TableCell>
        <TableCell>
          {checkIfNullable(component.deviceCalibration.manufacturer)}
        </TableCell>
        {component.deviceCalibration.calibrations !== null ? (
          component.deviceCalibration.calibrations
            .sort((a, b) => a.freq - b.freq)
            .map((calibration, calIndex) => {
              const difference =
                mainCalibrations?.[calIndex] !== undefined
                  ? calibration.appVolume - mainCalibrations[calIndex]
                  : "Brak danych";
              const tableRows = [
                {
                  label: "Głośność aplikacji:",
                  name: "appVolume",
                  value: calibration.appVolume,
                },
                {
                  label: "Głośność główna:",
                  name: "masterVolume",
                  value: calibration.masterVolume,
                },
                {
                  label: "Testowane względem [dB SPL]:",
                  name: "intensityTestedAgainst",
                  value: calibration.intensityTestedAgainst,
                },
                {
                  label: "Różnica:",
                  name: "difference",
                  value: difference,
                },
                {
                  label: "Maksymalna głośność aplikacji [dB SPL]:",
                  name: "maxIntensity",
                  value: calibration.maxIntensity,
                },
              ];
              return isDetailedData ? (
                <TableCell>
                  <Table>
                    <TableBody>
                      {tableRows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell component="th" scope="row">
                            <strong>{row.label}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {row.name === "maxIntensity"
                              ? checkIfNullableAndRound(
                                  Math.round(row.value * 10) / 10
                                )
                              : row.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableCell>
              ) : (
                <TableCell>
                  {Math.round(calibration.maxIntensity * 10) / 10 + " dB SPL"}
                </TableCell>
              );
            })
        ) : (
          <TableCell colSpan={4} style={{ textAlign: "center" }}>
            Brak Danych
          </TableCell>
        )}
      </React.Fragment>
    );
  },
};

export const modelConfig = {
  mainRowCells: [
    (data) => <TableCell key="manufacturer">{data.manufacturer}</TableCell>,
    (data) => <TableCell key="model">{data.model}</TableCell>,
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Data",
    "500 hz",
    "1000 hz",
    "2000 hz",
    "4000 hz",
  ],
  collapsibleRowRender: (
    component,
    index,
    { mainCalibrations, checkBoxes, handleCheckboxChange, isDetailedData }
  ) => {
    if (!component.deviceCalibration) {
      return (
        <TableCell colSpan={8}>
          Brak danych kalibracyjnych dla tego komponentu.
        </TableCell>
      );
    }

    const componentId = component.deviceCalibration.id;
    return (
      <React.Fragment key={index}>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          <Checkbox
            key={componentId}
            checked={checkBoxes[componentId] ?? false}
            onChange={() => handleCheckboxChange(componentId)}
          />
        </TableCell>
        <TableCell>
          {convertDate(component.deviceCalibration.createdAt)}
        </TableCell>
        {component.deviceCalibration.calibrations !== null ? (
          component.deviceCalibration.calibrations
            .sort((a, b) => a.freq - b.freq)
            .map((calibration, calIndex) => {
              const difference =
                mainCalibrations?.[calIndex] !== undefined
                  ? calibration.appVolume - mainCalibrations[calIndex]
                  : "Brak danych";
              const tableRows = [
                {
                  label: "Głośność aplikacji:",
                  name: "appVolume",
                  value: calibration.appVolume,
                },
                {
                  label: "Głośność główna:",
                  name: "masterVolume",
                  value: calibration.masterVolume,
                },
                {
                  label: "Testowane względem [dB SPL]:",
                  name: "intensityTestedAgainst",
                  value: calibration.intensityTestedAgainst,
                },
                {
                  label: "Różnica:",
                  name: "difference",
                  value: difference,
                },
                {
                  label: "Maksymalna głośność aplikacji [dB SPL]:",
                  name: "maxIntensity",
                  value: calibration.maxIntensity,
                },
              ];
              return isDetailedData ? (
                <TableCell>
                  <Table>
                    <TableBody>
                      {tableRows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell component="th" scope="row">
                            <strong>{row.label}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {row.name === "maxIntensity"
                              ? checkIfNullableAndRound(row.value)
                              : row.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableCell>
              ) : (
                <TableCell>
                  {Math.round(calibration.maxIntensity * 10) / 10 + " dB SPL"}
                </TableCell>
              );
            })
        ) : (
          <TableCell colSpan={4} style={{ textAlign: "center" }}>
            Brak Danych
          </TableCell>
        )}
      </React.Fragment>
    );
  },
};

export const brandConfig = {
  mainRowCells: [
    (data) => <TableCell key="manufacturer">{data.brand}</TableCell>,
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Model",
    "500 hz",
    "1000 hz",
    "2000 hz",
    "4000 hz",
  ],
  collapsibleRowRender: (
    component,
    index,
    {
      mainCalibrations,
      removeGroupHandler,
      checkBoxes,
      handleCheckboxChange,
      isDetailedData,
    }
  ) => {
    if (!component.deviceCalibration) {
      return (
        <TableCell colSpan={8}>
          Brak danych kalibracyjnych dla tego komponentu.
        </TableCell>
      );
    }
    const componentId = component.deviceCalibration.id;

    return (
      <React.Fragment key={index}>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          <Checkbox
            key={componentId}
            checked={checkBoxes[componentId] ?? false}
            onChange={() => handleCheckboxChange(componentId)}
          />
        </TableCell>
        <TableCell>{component.deviceCalibration.model}</TableCell>
        {component.deviceCalibration.calibrations !== null ? (
          component.deviceCalibration.calibrations
            .sort((a, b) => a.freq - b.freq)
            .map((calibration, calIndex) => {
              const difference =
                mainCalibrations?.[calIndex] !== undefined
                  ? calibration.appVolume - mainCalibrations[calIndex]
                  : "Brak danych";
              const tableRows = [
                {
                  label: "Głośność aplikacji:",
                  name: "appVolume",
                  value: calibration.appVolume,
                },
                {
                  label: "Głośność główna:",
                  name: "masterVolume",
                  value: calibration.masterVolume,
                },
                {
                  label: "Testowane względem [dB SPL]:",
                  name: "intensityTestedAgainst",
                  value: calibration.intensityTestedAgainst,
                },
                {
                  label: "Różnica:",
                  name: "difference",
                  value: difference,
                },
                {
                  label: "Maksymalna głośność aplikacji [dB SPL]:",
                  name: "maxIntensity",
                  value: calibration.maxIntensity,
                },
              ];
              return isDetailedData ? (
                <TableCell>
                  <Table>
                    <TableBody>
                      {tableRows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell component="th" scope="row">
                            <strong>{row.label}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {row.name === "maxIntensity"
                              ? checkIfNullableAndRound(row.value)
                              : row.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableCell>
              ) : (
                <TableCell>
                  {Math.round(calibration.maxIntensity * 10) / 10 + " dB SPL"}
                </TableCell>
              );
            })
        ) : (
          <TableCell colSpan={4} style={{ textAlign: "center" }}>
            Brak Danych
          </TableCell>
        )}
      </React.Fragment>
    );
  },
};

export const manufacturerConfig = {
  mainRowCells: [
    (data) => <TableCell key="manufacturer">{data.manufacturer}</TableCell>,
    (data) => <TableCell key="count">{data.components.length}</TableCell>,
  ],
  collapsibleHeaders: [
    "#",
    "Aktywna",
    "Marka",
    "500 hz",
    "1000 hz",
    "2000 hz",
    "4000 hz",
  ],
  collapsibleRowRender: (
    component,
    index,
    { mainCalibrations, checkBoxes, handleCheckboxChange, isDetailedData }
  ) => {
    if (!component.deviceCalibration) {
      return (
        <TableCell colSpan={8}>
          Brak danych kalibracyjnych dla tego komponentu.
        </TableCell>
      );
    }
    const componentId = component.deviceCalibration.id;

    return (
      <React.Fragment key={index}>
        <TableCell>{index + 1}</TableCell>
        <TableCell>
          <Checkbox
            key={componentId}
            checked={checkBoxes[componentId] ?? false}
            onChange={() => handleCheckboxChange(componentId)}
          />
        </TableCell>
        <TableCell>{component.deviceCalibration.brand}</TableCell>
        {component.deviceCalibration.calibrations !== null ? (
          component.deviceCalibration.calibrations
            .sort((a, b) => a.freq - b.freq)
            .map((calibration, calIndex) => {
              const difference =
                mainCalibrations?.[calIndex] !== undefined
                  ? calibration.appVolume - mainCalibrations[calIndex]
                  : "Brak danych";
              const tableRows = [
                {
                  label: "Głośność aplikacji:",
                  name: "appVolume",
                  value: calibration.appVolume,
                },
                {
                  label: "Głośność główna:",
                  name: "masterVolume",
                  value: calibration.masterVolume,
                },
                {
                  label: "Testowane względem [dB SPL]:",
                  name: "intensityTestedAgainst",
                  value: calibration.intensityTestedAgainst,
                },
                {
                  label: "Różnica:",
                  name: "difference",
                  value: difference,
                },
                {
                  label: "Maksymalna głośność aplikacji [dB SPL]:",
                  name: "maxIntensity",
                  value: calibration.maxIntensity,
                },
              ];
              return isDetailedData ? (
                <TableCell>
                  <Table>
                    <TableBody>
                      {tableRows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell component="th" scope="row">
                            <strong>{row.label}</strong>
                          </TableCell>
                          <TableCell align="right">
                            {row.name === "maxIntensity"
                              ? checkIfNullableAndRound(row.value)
                              : row.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableCell>
              ) : (
                <TableCell>
                  {Math.round(calibration.maxIntensity * 10) / 10 + " dB SPL"}
                </TableCell>
              );
            })
        ) : (
          <TableCell colSpan={4} style={{ textAlign: "center" }}>
            Brak Danych
          </TableCell>
        )}
      </React.Fragment>
    );
  },
};
